import { FormItem, Input, Textarea } from '@vkontakte/vkui';
import { useState } from 'react';
import { ABILITY_KIND_LABELS, type AbilityKind } from '../../api/types';
import { t } from '../../i18n';
import {
  Action,
  FieldLabel,
  FormActions,
  ImageField,
  SectionTitle,
  Segmented,
  type SegmentedOption,
} from '../../shared/ui';

/**
 * Форма заявки на способность или дебаф.
 *
 * Две вещи здесь принципиально разные и спрашиваются порознь: ЧТО заявляют
 * (вид — способность или дебаф) и ОТКУДА это взялось (ссылка на источник).
 * Раньше поле было одно на оба вопроса, и модель врала: дебаф прекрасно
 * прилетает вне квеста, а способность — из отыгрыша.
 *
 * Отсюда и блоки: сама заявка, потом откуда и до каких пор, потом картинка —
 * как у предмета и питомца, где текст тоже идёт перед фото.
 *
 * Стартовые способности сюда не относятся вовсе: они живут девятым пунктом
 * анкеты и правятся там же, вида у них нет.
 */

const KIND_OPTIONS: SegmentedOption<AbilityKind>[] = (
  Object.keys(ABILITY_KIND_LABELS) as AbilityKind[]
).map((kind) => ({ value: kind, label: ABILITY_KIND_LABELS[kind] }));

export interface AbilityFormValues {
  kind: AbilityKind;
  title: string;
  description: string;
  /** Ссылка на квест, пост или отыгрыш. Пустая строка = «не указано». */
  sourceLink: string;
  /** Дата в формате YYYY-MM-DD, как её отдаёт input[type=date]. Пусто = бессрочно. */
  expiresAt: string;
  /** Картинка выдачи: id загруженной строки images. Необязательна. */
  imageId: string | null;
}

export const EMPTY_ABILITY: AbilityFormValues = {
  kind: 'ability',
  title: '',
  description: '',
  sourceLink: '',
  expiresAt: '',
  imageId: null,
};

/**
 * Завтра по местному времени. Сервер читает дату без времени как полночь UTC
 * и требует будущего, поэтому сегодняшнюю он отбил бы.
 */
function tomorrow(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export interface AbilityFormProps {
  initial: AbilityFormValues;
  submitLabel: string;
  busy?: boolean;
  onSubmit: (values: AbilityFormValues) => void;
  onCancel: () => void;
}

export function AbilityForm({ initial, submitLabel, busy, onSubmit, onCancel }: AbilityFormProps) {
  const [values, setValues] = useState<AbilityFormValues>(initial);
  const patch = (next: Partial<AbilityFormValues>) => setValues((prev) => ({ ...prev, ...next }));

  const blocker = values.title.trim().length === 0 ? t('ability.error.titleRequired') : null;

  const canSubmit = blocker === null && busy !== true;

  return (
    <div>
      <SectionTitle>{t('ability.form.whatSection')}</SectionTitle>

      {/* Вариантов ровно два, и они взаимоисключающие: это значение поля, а не
          раздел списка, поэтому полоса формы, а не ряд вкладок. */}
      <FormItem top={<FieldLabel>{t('common.field.kind')}</FieldLabel>}>
        <Segmented
          value={values.kind}
          options={KIND_OPTIONS}
          disabled={busy}
          onChange={(kind) => patch({ kind })}
        />
      </FormItem>

      <FormItem top={<FieldLabel required>{t('common.field.title')}</FieldLabel>}>
        <Input
          value={values.title}
          disabled={busy}
          onChange={(event) => patch({ title: event.target.value })}
        />
      </FormItem>

      <FormItem top={<FieldLabel>{t('ability.field.description')}</FieldLabel>}>
        <Textarea
          rows={3}
          value={values.description}
          disabled={busy}
          onChange={(event) => patch({ description: event.target.value })}
        />
      </FormItem>

      <SectionTitle>{t('ability.form.sourceSection')}</SectionTitle>

      <FormItem
        top={
          <FieldLabel hint={t('ability.hint.source')}>{t('ability.field.sourceLink')}</FieldLabel>
        }
      >
        <Input
          value={values.sourceLink}
          placeholder={t('ability.field.sourcePlaceholder')}
          disabled={busy}
          onChange={(event) => patch({ sourceLink: event.target.value })}
        />
      </FormItem>

      <FormItem
        top={
          <FieldLabel hint={t('ability.hint.expiresAt')}>{t('ability.field.expiresAt')}</FieldLabel>
        }
      >
        <Input
          type="date"
          min={tomorrow()}
          value={values.expiresAt}
          disabled={busy}
          onChange={(event) => patch({ expiresAt: event.target.value })}
        />
      </FormItem>

      {/* Подпись у поля своя, вторым заголовком сверху её не дублируем, а имя
          берём от вида: у дебафа на картинке не способность. */}
      <FormItem top={<FieldLabel>{t('common.field.image')}</FieldLabel>}>
        <ImageField
          label={ABILITY_KIND_LABELS[values.kind]}
          hint={t('ability.hint.image')}
          imageId={values.imageId}
          onChange={(imageId) => patch({ imageId })}
          disabled={busy}
        />
      </FormItem>

      {/* В строку, как в макете: главное действие тянется, отказ от него
          стоит по содержимому. */}
      <FormActions>
        <Action
          tone="action"
          size="l"
          stretched
          loading={busy}
          disabled={!canSubmit}
          onClick={() => onSubmit(values)}
        >
          {submitLabel}
        </Action>
        <Action tone="quiet" size="l" stretched disabled={busy} onClick={onCancel}>
          {t('common.action.cancel')}
        </Action>
      </FormActions>
    </div>
  );
}
